import { useState } from 'react';
import { PageHeader } from '../components/Layout.jsx';
import { Alert, Card, Empty, ErrorState, Input, Loading, Segmented, Select, Stat, Textarea } from '../components/ui.jsx';
import { Modal, ConfirmModal } from '../components/Modal.jsx';
import { IconPlus } from '../components/Icons.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../lib/toast.jsx';
import { categoryText, t } from '../lib/i18n.js';
import { formatDate, money, num, startOfMonth, today } from '../lib/format.js';

export default function Expenses() {
  const toast = useToast();
  const { isAdmin } = useAuth();
  const [from, setFrom] = useState(startOfMonth());
  const [to, setTo] = useState(today());
  const [category, setCategory] = useState('');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const categories = useApi('/api/expenses/categories');
  const exchangeCompanies = useApi('/api/exchange/companies', { include_inactive: true });
  const { data, error, loading, reload } = useApi('/api/expenses', { from, to, category, limit: 300 });

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/api/expenses/${deleting.id}`);
      toast.success(t.expense.deleted);
      setDeleting(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t.expense.title}
        subtitle={t.expense.subtitle}
        actions={
          <button type="button" className="btn btn-sm btn-primary no-print" onClick={() => setEditing({})}>
            <IconPlus /> {t.common.add}
          </button>
        }
      />

      <div className="page">
        <Card>
          <div className="filters">
            <Input label={t.common.from} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Input label={t.common.to} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            <Select
              label={t.expense.category}
              placeholder={t.expense.allCategories}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              options={(categories.data || []).map((c) => ({ value: c, label: categoryText(c) }))}
            />
          </div>
        </Card>

        {data?.totals && (
          <div className="stat-grid">
            <Stat label={t.expense.entryCount} value={num(data.totals.count)} />
            <Stat label={t.expense.totalIqd} value={money(data.totals.total_iqd, 'IQD')} />
            <Stat label={t.expense.totalUsd} value={money(data.totals.total_usd, 'USD')} />
          </div>
        )}

        {data?.by_category?.length > 0 && (
          <Card title={t.expense.byCategory}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t.expense.category}</th>
                    <th className="num">{t.report.iqd}</th>
                    <th className="num">{t.report.usd}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_category.map((row) => (
                    <tr key={row.category}>
                      <td>{categoryText(row.category)}</td>
                      <td className="num">{num(row.total_iqd)}</td>
                      <td className="num">{num(row.total_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <ErrorState error={error} onRetry={reload} />
        {loading && !data && <Loading />}

        {data && data.rows.length === 0 && (
          <Card>
            <Empty
              title={t.expense.none}
              action={
                <button type="button" className="btn btn-primary" onClick={() => setEditing({})}>
                  <IconPlus /> {t.expense.addOne}
                </button>
              }
            />
          </Card>
        )}

        {data && data.rows.length > 0 && (
          <div className="record-list">
            {data.rows.map((row) => (
              <div key={row.id} className="record">
                <div className="record-top">
                  <span className="title">{row.description || categoryText(row.category)}</span>
                  <span className="amount">{money(row.amount, row.currency)}</span>
                </div>
                <div className="record-meta">
                  <span className="badge">{categoryText(row.category)}</span>
                  <span>{formatDate(row.spent_on)}</span>
                  {row.paid_by && <span>{t.expense.paidByShort(row.paid_by)}</span>}
                  {row.exchange_company_name && (
                    <span className="badge muted">{t.expense.paidViaCompany(row.exchange_company_name)}</span>
                  )}
                </div>
                <div className="btn-row no-print" style={{ marginTop: 4 }}>
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(row)}>
                    {t.common.edit}
                  </button>
                  {isAdmin && (
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeleting(row)}>
                      {t.common.delete}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <ExpenseForm
          expense={editing}
          categories={categories.data || []}
          exchangeCompanies={exchangeCompanies.data || []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success(t.expense.saved);
            reload();
            categories.reload();
          }}
        />
      )}

      {deleting && (
        <ConfirmModal
          title={t.expense.deleteTitle}
          message={t.expense.deleteMessage(
            money(deleting.amount, deleting.currency),
            formatDate(deleting.spent_on),
          )}
          onConfirm={remove}
          onClose={() => setDeleting(null)}
          busy={busy}
        />
      )}
    </>
  );
}

function ExpenseForm({ expense, categories, exchangeCompanies, onClose, onSaved }) {
  const isEdit = Boolean(expense.id);
  const [form, setForm] = useState({
    spent_on: expense.spent_on || today(),
    category: expense.category || 'متفرقات',
    description: expense.description || '',
    amount: expense.amount || '',
    currency: expense.currency || 'IQD',
    paid_by: expense.paid_by || '',
    notes: expense.notes || '',
    paidVia: expense.exchange_company_name ? 'company' : 'cash',
    exchange_company_id: '',
    exchange_kind: 'transfer',
    exchange_reference: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    setError(null);
    const payload = { ...form };
    if (!isEdit && form.paidVia === 'company' && form.exchange_company_id) {
      payload.exchange_company_id = Number(form.exchange_company_id);
      payload.exchange_kind = form.exchange_kind;
      payload.exchange_reference = form.exchange_reference;
    }
    try {
      if (isEdit) await api.patch(`/api/expenses/${expense.id}`, payload);
      else
        await api.post('/api/expenses', payload, {
          queueOffline: true,
          label: `${t.expense.title} — ${categoryText(form.category)}`,
        });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isEdit ? t.expense.editTitle : t.expense.newTitle}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !(Number(form.amount) > 0)}>
            {busy ? <span className="spinner" /> : t.common.save}
          </button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      <Input
        label={t.common.date}
        required
        type="date"
        value={form.spent_on}
        onChange={(e) => set({ spent_on: e.target.value })}
      />
      {/* Free text with suggestions, so a category nobody anticipated can still
          be typed in rather than forcing everything into "متفرقات". */}
      <Input
        label={t.expense.category}
        required
        list="expense-categories"
        value={form.category}
        onChange={(e) => set({ category: e.target.value })}
        hint={t.expense.categoryHint}
      />
      <datalist id="expense-categories">
        {categories.map((c) => (
          <option key={c} value={categoryText(c)} />
        ))}
      </datalist>
      <Input
        label={t.expense.description}
        value={form.description}
        onChange={(e) => set({ description: e.target.value })}
      />
      <div className="form-grid cols-2">
        <Input
          label={t.common.amount}
          required
          inputMode="decimal"
          value={form.amount}
          onChange={(e) => set({ amount: e.target.value.replace(/[^\d.]/g, '') })}
        />
        <Select
          label={t.common.currency}
          value={form.currency}
          onChange={(e) => set({ currency: e.target.value })}
          options={[
            { value: 'IQD', label: t.report.iqd },
            { value: 'USD', label: t.report.usd },
          ]}
        />
      </div>
      <Input label={t.expense.paidBy} value={form.paid_by} onChange={(e) => set({ paid_by: e.target.value })} />

      {isEdit ? (
        expense.exchange_company_name && (
          <Alert tone="info">{t.expense.paidViaCompany(expense.exchange_company_name)}</Alert>
        )
      ) : (
        <>
          <Segmented
            label={t.expense.paidVia}
            value={form.paidVia}
            onChange={(v) => set({ paidVia: v })}
            options={[
              { value: 'cash', label: t.shared.cash },
              { value: 'company', label: t.expense.viaExchangeCompany },
            ]}
          />
          {form.paidVia === 'company' && (
            <>
              <Select
                label={t.exchange.company}
                required
                placeholder={t.exchange.allCompanies}
                value={form.exchange_company_id}
                onChange={(e) => set({ exchange_company_id: e.target.value })}
                options={exchangeCompanies.map((c) => ({ value: c.id, label: c.name }))}
              />
              <Segmented
                label={t.expense.exchangeKind}
                value={form.exchange_kind}
                onChange={(v) => set({ exchange_kind: v })}
                options={[
                  { value: 'transfer', label: t.expense.exchangeKindTransfer },
                  { value: 'deposit', label: t.expense.exchangeKindDeposit },
                  { value: 'withdrawal', label: t.expense.exchangeKindWithdrawal },
                ]}
                hint={
                  form.exchange_kind === 'transfer'
                    ? t.expense.exchangeKindTransferHint
                    : form.exchange_kind === 'deposit'
                      ? t.expense.exchangeKindDepositHint
                      : t.expense.exchangeKindWithdrawalHint
                }
              />
              <Input
                label={t.exchange.reference}
                value={form.exchange_reference}
                onChange={(e) => set({ exchange_reference: e.target.value })}
              />
            </>
          )}
        </>
      )}

      <Textarea label={t.common.notes} value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} />
    </Modal>
  );
}
